import React, { useContext, useState, useEffect, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { map, uniq, sortBy, union, isEmpty } from 'underscore';
import {
  useDefaultEditorStyle,
  getComponentById,
  useFloatingBannerMessage,
  WEBSITE_SOURCE_DATA_KEY_MAPPING,
} from '@bingads-webui-campaign-react/smart-component-util';
import {
  DataServiceContext,
  useEditorStatusContext,
  useSiteConfig,
  editorStatusLoadingWrapperForPromise,
  useInstrumentationServiceContext,
  useIsSmartPageImprovedReviewsEnabled,
} from '@bingads-webui-campaign-react/smart-page-context';
import { withDefaultStyles } from '@bingads-webui-react/with-default-styles';
import {
  facebookPageInfoFilter,
  importFromFacebook,
  hasFBOAuthError,
} from '@bingads-webui-campaign-react/smart-page-facebook-import-dialog';
import { ItemPicker, CardDialog } from '@bingads-webui-campaign-react/smart-component-card';

import { CommandBarButton } from '@fluentui/react/lib/Button';
import { MessageBar } from '@fluentui/react/lib/MessageBar';

import { SelectReviewDialog } from './components/select-review-dialog';
import { SmartReviewCard } from './components/smart-review-card';
import { generateReview, mapReviewToCard } from './components/review-helper';
import { useDataState } from './components/use-data-state';
import { proxyReadReviewsComponentConfig } from './component-config-reader';
import { covertConfigToModel } from './data-config';
import { REVIEW_TYPE, MaxReviewsCount, authorLimit, softLimit, hardLimit } from './components/constants';

const componentKey = WEBSITE_SOURCE_DATA_KEY_MAPPING.Reviews;

const editableConditionCheck = review => review.type === REVIEW_TYPE.CUSTOM_REVIEW;

const getStyles = () => ({
  reviewsListLabel: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    margin: '0px 38px 28px 20px',
  },
  reviewsList: {
    paddingTop: 7,
  },
  addBtn: {
    backgroundColor: 'transparent',
    '&:enabled': {
      color: '#0063B1',
    },
  },
  dropDownBtn: {
    height: 26,
  },
  noData: {
    background: '#F3F2F0',
    padding: '24px 22px',
  },
  commonIcon: {
    color: '#0078D4',
    fontSize: '16px',
    lineHeight: '16px',
    margin: '0px 4px',
    fontFamily: 'MsAds MDL2 Assets',
    fontStyle: 'normal',
  },
  fbIcon: {
    '&:before': {
      content: '"\\ECB3"',
    },
  },
  editCreateIcon: {
    '&:before': {
      content: '"\\F3C9"',
    },
  },
});

export const UnStyledSmartReviewEditor = ({
  i18n,
  componentId,
  classes,
  gotoSettings,
}) => {
  const {
    titleClassName,
    descriptionClassName,
    sectionTitleClassName,
    infoMessageBarClassName,
  } = useDefaultEditorStyle();
  const isSmartPageImprovedReviewsEnabled = useIsSmartPageImprovedReviewsEnabled();
  const [, dispatchEditorStatus] = useEditorStatusContext();
  const { siteConfig, dispatchSiteConfig } = useSiteConfig();
  const { smartPageService, hostSystemScenario } = useContext(DataServiceContext);
  const [startFetchFbReviews, setStartFetchFbReviews] = useState(false);
  const [isFbSupported, setIsFbSupported] = useState(false);
  const [isFbInitializing, setIsFbInitializing] = useState(true);
  const [allFbReviews, setAllFbReviews] = useState([]);
  const [targetReview, setTargetReview] = useState(undefined);
  const [showFbImportDialog, setShowFbImportDialog] = useState(false);
  const [showCreateReviewDialog, setShowCreateReviewDialog] = useState(false);
  const { showWarning } = useFloatingBannerMessage(dispatchEditorStatus);
  const logger = useInstrumentationServiceContext();

  const componentConfig = getComponentById(siteConfig, componentId) || {};
  let reviews;
  if (isSmartPageImprovedReviewsEnabled) {
    reviews = proxyReadReviewsComponentConfig(siteConfig);
  }

  if (!reviews || isEmpty(reviews)) {
    ({ reviews = [] } = componentConfig);
  }

  const [selectedReviews, setSelectedReviews] = useState(reviews);
  const { noDataMsgKey, noData } = useDataState(allFbReviews, selectedReviews);

  const onSaveChange = useCallback(({ newSelections, imageResult }) => {
    const allSelectedReviews = sortBy(
      uniq(union(newSelections, selectedReviews || []), review => review.id),
      review => new Date(review.createdTime).getTime() || 0
    ).reverse();
    if (isSmartPageImprovedReviewsEnabled) {
      dispatchSiteConfig({
        type: 'UPDATE_WEBSITESOURCEDATA_BY_NAME',
        payload: {
          name: componentKey,
          value: allSelectedReviews.map(covertConfigToModel),
        },
      });
    }

    dispatchSiteConfig({
      type: 'UPDATE_COMPONENT_BY_ID',
      payload: {
        ...componentConfig,
        reviews: allSelectedReviews,
      },
    });

    dispatchSiteConfig({
      type: 'UPDATE_IMAGES',
      payload: {
        imagesToAdd: imageResult,
      },
    });

    setSelectedReviews(allSelectedReviews);
    setShowFbImportDialog(false);
    setShowCreateReviewDialog(false);
    setTargetReview(undefined);
  }, [componentConfig, dispatchSiteConfig, selectedReviews, isSmartPageImprovedReviewsEnabled]);

  const onEdit = useCallback((index) => {
    if (index >= 0 && index < selectedReviews.length) {
      setTargetReview(selectedReviews[index]);
      setShowCreateReviewDialog(true);
    }
  }, [selectedReviews]);

  const onDelete = useCallback((index) => {
    setSelectedReviews((selectedReviewState) => {
      const [deletedReview] = selectedReviewState.splice(index, 1);

      // Reassign the original fb src to picture
      // so the deleted review could still display correctly
      // without using Bing image id in current session
      deletedReview.reviewer.picture = deletedReview.reviewer.fbPictureSrc;
      delete deletedReview.reviewer.fbPictureSrc;

      dispatchSiteConfig({
        type: 'UPDATE_IMAGES',
        payload: {
          imageIdsToRemove: deletedReview.reviewer && [deletedReview.reviewer.imageId],
        },
      });

      if (isSmartPageImprovedReviewsEnabled) {
        dispatchSiteConfig({
          type: 'UPDATE_WEBSITESOURCEDATA_BY_NAME',
          payload: {
            name: componentKey,
            value: selectedReviewState.map(covertConfigToModel),
          },
        });
      }

      dispatchSiteConfig({
        type: 'UPDATE_COMPONENT_BY_ID',
        payload: {
          ...componentConfig,
          reviews: selectedReviewState,
        },
      });
      const newState = [...selectedReviewState];

      return newState;
    });
  }, [componentConfig, dispatchSiteConfig, isSmartPageImprovedReviewsEnabled]);

  const openDialog = useCallback(isFbImport => () => {
    if (selectedReviews.length < MaxReviewsCount) {
      if (isFbImport) {
        setStartFetchFbReviews(true);
        setShowFbImportDialog(true);
      } else {
        setShowCreateReviewDialog(true);
      }
    }
  }, [selectedReviews]);

  const closeCreateReviewDialog = useCallback(() => {
    setShowCreateReviewDialog(false);
    setTargetReview(undefined);
  }, []);

  useEffect(() => {
    if (!isSmartPageImprovedReviewsEnabled || startFetchFbReviews) {
      importFromFacebook(
        siteConfig,
        smartPageService,
        facebookPageInfoFilter.ratings,
        hostSystemScenario
      )
        .finally(editorStatusLoadingWrapperForPromise(dispatchEditorStatus))
        .then(({ reviews: fbReviews, businessName }) => {
          setAllFbReviews(map(fbReviews, review => ({
            recommendationType: review.RecommendationType,
            reviewText: review.ReviewText,
            reviewer: {
              name: review.Reviewer.Name,
              picture: review.Reviewer.Picture.Url,
            },
            createdTime: review.CreatedTime,
            businessName,
            id: `${review.CreatedTime}-${review.Reviewer.Id}`,
          })));
          setIsFbSupported(true);
        })
        .catch((err) => {
          if (err) {
            if (hasFBOAuthError(err)) {
              logger.logTrace('Encounter Facebook OAuth error while importing FB reviews');
              showWarning(i18n.getString(_TL_('We could not access your Facebook profile, please reconnect it in Settings.')));
              setIsFbSupported(false);
            } else if (err.message !== 'NO_FB_AUTH') {
              logger.logError(`Import reviews from facebook failed. ${err}, error string: ${JSON.stringify(err)}`);
              showWarning(i18n.getString(_TL_('We were not able to find reviews from this Facebook page. Please try again or reimport your Facebook Page.')));
              setIsFbSupported(true);
            }
          }
        })
        .finally(() => setIsFbInitializing(false));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startFetchFbReviews, isSmartPageImprovedReviewsEnabled]);

  const menuProps = useMemo(() => (isSmartPageImprovedReviewsEnabled ?
    ({
      items: [
        {
          key: 'fbImport',
          text: i18n.getString(_TL_('Import from Facebook')),
          iconProps: {
            className: classnames(classes.commonIcon, classes.fbIcon),
          },
          'data-automation-id': 'fb-import-option', // optional
          onClick: openDialog(true),
        },
        {
          key: 'createMyOwn',
          text: i18n.getString(_TL_('Create my own')),
          iconProps: {
            className: classnames(classes.commonIcon, classes.editCreateIcon),
          },
          'data-automation-id': 'create-my-own-option', // optional
          onClick: openDialog(false),
        },
      ],
    }) : undefined), [
    i18n,
    isSmartPageImprovedReviewsEnabled,
    openDialog,
    classes,
  ]);

  const reviewEditorTexts = useMemo(() => {
    if (isSmartPageImprovedReviewsEnabled) {
      return {
        titleText: i18n.getString(_TL_('Reviews')),
        descriptionText: i18n.getString(_TL_('Create new reviews or import them directly from your Facebook Business Page to your Smart Page. You may publish a maximum of {{max}} reviews on your Smart Page.'), { max: MaxReviewsCount }),
        addBtnText: i18n.getString(_TL_('Add reviews')),
        reviewListText: i18n.getString(_TL_('Your reviews')),
      };
    }

    return {
      titleText: i18n.getString(_TL_('Reviews')),
      descriptionText: i18n.getString(_TL_('Add up to {{max}} customer reviews from Facebook to your website.'), { max: MaxReviewsCount }),
      addBtnText: i18n.getString(_TL_('Add reviews')),
      reviewListText: i18n.getString(_TL_('Reviews from the last 2 years')),
    };
  }, [isSmartPageImprovedReviewsEnabled, i18n]);

  const cardDialogTexts = {
    maximumCharacterErrorText: _TL_('Your review is currently {{length}} characters. How customers view your Smart Page may result in any review longer than 200 characters being cut off. For this reason, we recommend shortening your review to under {{softLimit}} characters.'),
    creationTitleText: i18n.getString(_TL_('Create your own review')),
    editTitleText: i18n.getString(_TL_('Edit your own review')),
    authorSubtitleText: i18n.getString(_TL_('What is the name of the reviewer?')),
    messageSubtitleText: i18n.getString(_TL_('What is the review text?')),
    primaryButtonText: i18n.getString(_TL_('Add review')),
  };

  return (
    <div className={classes.reviewsEditorContainer}>
      <h2 className={titleClassName}>
        {reviewEditorTexts.titleText}
      </h2>
      <div className={descriptionClassName}>
        {reviewEditorTexts.descriptionText}
      </div>
      <div className={classnames(classes.reviewsList, 'smart-page-reviews-list')}>
        <div className={classes.reviewsListLabel}>
          <span className={sectionTitleClassName}>{reviewEditorTexts.reviewListText}</span>
          <CommandBarButton
            iconProps={{ iconName: 'Add' }}
            text={reviewEditorTexts.addBtnText}
            onClick={() => { setShowFbImportDialog(true); }}
            className={classnames(classes.addBtn, classes.dropDownBtn)}
            disabled={(selectedReviews && selectedReviews.length === MaxReviewsCount) ?
              true : (noData && !isSmartPageImprovedReviewsEnabled)}
            menuProps={menuProps}
          />
        </div>
        {
          noDataMsgKey ?
            <MessageBar className={classnames(infoMessageBarClassName, classes.noData, 'smart-page-review-no-data')} isMultiline>
              {
                isSmartPageImprovedReviewsEnabled ?
                i18n.getString(_TL_('You haven\'t imported any Facebook reviews. Select "Add reviews" to import or create your own review.')) :
                i18n.getString(noDataMsgKey)
              }
            </MessageBar> :
            <ItemPicker
              items={selectedReviews}
              editable={isSmartPageImprovedReviewsEnabled}
              editableConditionCheck={editableConditionCheck}
              i18n={i18n}
              ItemComponent={SmartReviewCard}
              onEdit={onEdit}
              onDelete={onDelete}
              editButtonLabel={i18n.getString(_TL_('Edit review'))}
              getDeleteButtonAriaLabel={
                index => i18n.getString(_TL_('Delete review {{number}}'), { number: index })
              }
              getEditButtonAriaLabel={
                index => i18n.getString(_TL_('Edit review {{number}}'), { number: index })
              }
            />
        }
      </div>
      {
        showFbImportDialog &&
        <SelectReviewDialog
          i18n={i18n}
          onAdd={onSaveChange}
          onCancel={() => { setShowFbImportDialog(false); }}
          isFbSupported={isFbSupported}
          isFbInitializing={isFbInitializing}
          gotoSettings={gotoSettings}
          reviews={allFbReviews}
          preselectedReviews={map(selectedReviews || [], review => review.id)}
        />
      }
      {
        showCreateReviewDialog && isSmartPageImprovedReviewsEnabled &&
        <CardDialog
          ItemComponent={SmartReviewCard}
          targetItem={targetReview}
          generateItemProperty={generateReview}
          mapItemToCard={mapReviewToCard}
          authorCharacterLimit={authorLimit}
          characterSoftLimit={softLimit}
          characterHardLimit={hardLimit}
          labelTexts={cardDialogTexts}
          i18n={i18n}
          onAdd={onSaveChange}
          onCancel={closeCreateReviewDialog}
          gotoSettings={gotoSettings}
          cardType="review"
          supportImageInput={false}
          supportAuthorInput
        />
      }
    </div>
  );
};

UnStyledSmartReviewEditor.propTypes = {
  i18n: PropTypes.shape({
    getString: PropTypes.func.isRequired,
  }).isRequired,
  componentId: PropTypes.string.isRequired,
  classes: PropTypes.shape({
    reviewsListLabel: PropTypes.string.isRequired,
  }).isRequired,
  gotoSettings: PropTypes.func.isRequired,
};

export const SmartReviewEditor = withDefaultStyles(
  UnStyledSmartReviewEditor,
  getStyles
);
